import { MS_24_HOURS } from '@libs/constants/app.constants';
import { CREDIT_CARD_TYPE, ICreditCard } from '@libs/interfaces';
import { each, omit, map, isUndefined, isNull, chunk, range, uniq, remove, isNumber } from 'lodash';
import { Observable } from 'rxjs';
import { GetTimestamp00Hours } from './dates.functionals';

export const map_lodash = map;
export const GET_TOTAL_AMOUNT = (ele): number => ele.reduce((acum, d) => +d.amount + acum, 0);
export const GET_MAX_AMOUNT = ele => ele.reduce((acum, d) => Math.max(+d.amount, acum), 0);
export const GET_MIN_AMOUNT = ele => ele.reduce((acum, d) => Math.min(+d.amount, acum), 1000000000000000000);
export const ADD_TOTAL_AMOUNT_PERC = data => {
  const total = GET_TOTAL_AMOUNT(data);
  return data.map(ele => {
    ele.perc = +ele.amount / total;
    return ele;
  });
};

export const Base64String = (arrayBuffer): string => {
  var binary = '';
  var bytes = new Uint8Array(arrayBuffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64;
};
export const BlobToBase64 = (blob): Observable<string> =>
  new Observable(observer => {
    const reader = new FileReader();
    reader.onloadend = () => {
      observer.next(reader.result as string);
      observer.complete();
    };
    reader.readAsDataURL(blob);
  });
export const DataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = (arr.length == 1 ? 'data:image/*;base64' : arr[0]).match(/:(.*?);/)[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};
export const PdfToData = (file: any): Observable<ArrayBuffer> => {
  return new Observable(observer => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      observer.next(e.target.result);
      observer.complete();
    };
    reader.readAsArrayBuffer(file);
  });
};
export const AsyncForEach = async (array, callback) => {
  for (let index = 0; index < array.length; index++) await callback(array[index], index, array);
};
export const AsyncMap = async (array, callback) => {
  const r = [];
  for (let index = 0; index < array.length; index++) r.push(await callback(array[index], index, array));
  return r;
};
export const AsyncFilter = async (array, callback) => {
  const r = [];
  for (let index = 0; index < array.length; index++)
    (await callback(array[index], index, array)) && r.push(array[index]);
  return r;
};
export const SetFilter = (set, callback: Function) => {
  const r = [];
  for (const e of set) callback(e) && r.push(e);
  return r;
};

export const AcumulateArrayObjectsProps = (arr: any[], avoidFields: string[] = []): any => {
  const res = arr.reduce((acum, ele) => {
    acum &&
      each(acum, (e, key) => {
        if (!avoidFields.find(e => e == key)) acum[key] += ele[key];
      });
    return acum || { ...ele };
  }, null);
  return omit(res, avoidFields);
};

export const iOS = (): boolean => {
  const isOsx: boolean = ['iPad', 'iPhone'].includes(navigator.platform);
  const d: boolean = /iphone|ipad/.test(navigator.userAgent.toLowerCase());
  const t: boolean = navigator.userAgent.includes('Mac') && Boolean(document?.ontouchend);
  const r = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isOsx || d || t || r;
};
export const Android = () => /Android/i.test(navigator.userAgent);

export const GET_TIMESTAMPS_PREV_WEEKS = (numPrevWeeks: number) => {
  const today = GetTimestamp00Hours(Date.now());
  const today_week_day = new Date(today).getUTCDay();
  const this_week_ts =
    today_week_day == 0
      ? [today]
      : range(0, today_week_day)
          .map(i => today - MS_24_HOURS * i)
          .reverse();
  const prev_weeks = chunk(
    range(0, numPrevWeeks * 7)
      .map(i => this_week_ts[0] - MS_24_HOURS - MS_24_HOURS * i)
      .reverse(),
    7,
  );
  prev_weeks.push(this_week_ts);
  return prev_weeks;
};

export const GenerateStringPermutations = (s: string, repetition = false) => {
  const arr = s.split('');
  if (arr.length < 2) return arr.slice();

  const factorial = [1];
  for (var i = 1; i <= arr.length; i++) factorial.push(factorial[factorial.length - 1] * i);

  const allPerms = [];
  for (let permNumber = 0; permNumber < factorial[factorial.length - 1]; permNumber++) {
    const unused = arr.slice();
    const nextPerm = [];
    while (unused.length) {
      const nextIndex = Math.floor((permNumber % factorial[unused.length]) / factorial[unused.length - 1]);
      nextPerm.push(unused[nextIndex]);
      unused.splice(nextIndex, 1);
    }
    allPerms.push(nextPerm);
  }
  const result = allPerms.map(e => e.join(''));
  return repetition ? result : uniq(result);
};
export const isEven = n => n % 2 == 0;

export const isOdd = n => Math.abs(n % 2) == 1;

export const SpreadValuesInterval = (lastval: number, newval: number, increasing: boolean): number[] => {
  if (isUndefined(lastval) || isNull(lastval) || isUndefined(newval) || isNull(newval)) return [];
  else if (lastval == newval) return [newval];
  const signo = increasing ? 1 : -1;
  const result = [lastval];
  let i = lastval;
  const diff = Math.abs(lastval - newval);
  let counter = 0;
  do {
    i = i + signo;
    if (i < 0) {
      i = 99;
    } else if (i > 99 && increasing) {
      i = 0;
    }
    result.push(i);
  } while (i != newval && ++counter < 110);
  return result;
};
export const DetectCardType = (number): any => {
  var re = {
    electron: /^(4026|417500|4405|4508|4844|4913|4917)\d+$/,
    maestro: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$/,
    amex: /^3[47][0-9]{13}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
    diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    dankort: /^(5019)\d+$/,
    interpayment: /^(636)\d+$/,
    unionpay: /^(62|88)\d+$/,
    elo: /^((((636368)|(438935)|(504175)|(451416)|(636297))\d{0,10})|((5067)|(4576)|(4011))\d{0,12})$/,
    hipercard: /^(606282\d{10}(\d{3})?)|(3841\d{15})$/,
  };

  for (var key in re) {
    if (re[key].test(number)) {
      return CREDIT_CARD_TYPE[key];
    }
  }
};
export const CardTypeImage = (type: CREDIT_CARD_TYPE) => {
  switch (type) {
    case CREDIT_CARD_TYPE.electron:
      return '/assets/payments/cc_images/electron.JPG';
    case CREDIT_CARD_TYPE.maestro:
      return '/assets/payments/cc_images/maestro.JPG';
    case CREDIT_CARD_TYPE.visa:
      return '/assets/payments/cc_images/visa.JPG';
    case CREDIT_CARD_TYPE.mastercard:
      return '/assets/payments/cc_images/mastercard.JPG';
    case CREDIT_CARD_TYPE.amex:
      return '/assets/payments/cc_images/amex.png';
    case CREDIT_CARD_TYPE.discover:
      return '/assets/payments/cc_images/discover.JPG';
    case CREDIT_CARD_TYPE.jcb:
      return '/assets/payments/cc_images/jcb.JPG';
    default:
      return '/assets/payments/cc_images/empty.png';
  }
};
export const AddCcFlag = (cc: ICreditCard): string => {
  const type: CREDIT_CARD_TYPE = DetectCardType(cc.number);
  return CardTypeImage(type);
};
export const EmailPatternBetter = /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/;
export const ValidateEmail = email => EmailPatternBetter.test(String(email).toLowerCase());

export const RemoveArrayElement = (arr: any[], ele: any) => {
  const _arr = [...arr];
  remove(_arr, _ele => _ele == ele);
  return _arr;
};
export const AreTwoNumberArrayEqual = (arr1: any[], arr2: any[]): boolean =>
  arr1.length != arr2.length
    ? false
    : arr1.reduce((acum: boolean, value: number, index: number) => (value != arr2[index] ? false : acum), true);

export const RemoveNoNumbers = (txt: string): string => {
  const splitted = txt.split('').map(e => +e);
  remove(splitted, ele => isNaN(ele));
  remove(splitted, ele => !isNumber(ele));
  return splitted.join('');
};

export const EnumToArray = (MeuEnum): string[] => Object.entries(MeuEnum).map(e => e[1] as string);
